import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Layers, Grid3X3, Monitor, RefreshCw, Plus, Database, CloudDownload, Building2 } from 'lucide-react';
import ERPAdvancedGrid    from './erp/ERPAdvancedGrid';
import useERPContextMenu  from './erp/ERPContextMenu';
import { erpToast }       from './erp/ERPToast';
import '../styles/erp-theme.css';
import { API, apiFetch } from '../lib/api';

// ─────────────────────────────────────────────────────────────────────────────
// Entity definitions — column schemas, identity field, form fields
// ─────────────────────────────────────────────────────────────────────────────
const ENTITIES = {
  modules: {
    label:    'Modules',
    icon:     Layers,
    endpoint: '/uams/modules',
    idField:  'ModuleId',
    nameField:'ModuleName',
    columns: [
      { field: 'ModuleId',    header: 'ID',          visible: true, frozen: true },
      { field: 'ModuleName',  header: 'Module Name', visible: true },
      { field: 'Description', header: 'Description', visible: true },
      { field: 'AppType',     header: 'AppType',     visible: false },
      { field: 'Active',      header: 'Active',      visible: true },
      { field: 'Status',      header: 'Status',      visible: true },
      { field: 'SortOrder',   header: 'Sort',        visible: false },
      { field: 'LocalNote',   header: 'Note',        visible: false },
      { field: 'ModifiedAt',  header: 'Modified',    visible: true },
    ],
    formFields: (refs) => ([
      { name: 'moduleId',    label: 'Module ID (leave blank for auto)', type: 'number', editableOnAdd: true, editableOnEdit: false },
      { name: 'moduleName',  label: 'Module Name',  type: 'text',   required: true },
      { name: 'description', label: 'Description',  type: 'textarea' },
      { name: 'appType',     label: 'App Type',     type: 'number' },
      { name: 'sortOrder',   label: 'Sort Order',   type: 'number' },
      { name: 'active',      label: 'Active',       type: 'checkbox', default: true },
      { name: 'status',      label: 'Status',       type: 'text',     default: 'Active' },
      { name: 'localNote',   label: 'Local Note',   type: 'text' }
    ]),
    payloadFromForm: (form, mode) => {
      const out = { ...form };
      if (mode === 'edit') delete out.moduleId;
      if (out.moduleId === '' || out.moduleId == null) delete out.moduleId;
      ['appType', 'sortOrder'].forEach(k => { if (out[k] === '' || out[k] == null) delete out[k]; });
      return out;
    },
    fromRow: (r) => ({
      moduleId:    r.ModuleId,
      moduleName:  r.ModuleName || '',
      description: r.Description || '',
      appType:     r.AppType ?? '',
      sortOrder:   r.SortOrder ?? '',
      active:      r.Active === true || r.Active === 1,
      status:      r.Status || 'Active',
      localNote:   r.LocalNote || ''
    })
  },
  submodules: {
    label:    'SubModules',
    icon:     Grid3X3,
    endpoint: '/uams/submodules',
    idField:  'SubModuleId',
    nameField:'SubModuleName',
    columns: [
      { field: 'SubModuleId',   header: 'SubModule ID',   visible: true, frozen: true },
      { field: 'ParentModuleId',header: 'Parent Module',  visible: true },
      { field: 'SubModuleName', header: 'Name',           visible: true },
      { field: 'Description',   header: 'Description',    visible: true },
      { field: 'Active',        header: 'Active',         visible: true },
      { field: 'Status',        header: 'Status',         visible: true },
      { field: 'SortOrder',     header: 'Sort',           visible: false },
      { field: 'LocalNote',     header: 'Note',           visible: false },
      { field: 'ModifiedAt',    header: 'Modified',       visible: true },
    ],
    formFields: (refs) => ([
      { name: 'subModuleId',    label: 'SubModule ID',     type: 'text',   required: true, editableOnAdd: true, editableOnEdit: false },
      { name: 'parentModuleId', label: 'Parent Module',    type: 'select', required: true, optionsSource: 'modules' },
      { name: 'subModuleName',  label: 'SubModule Name',   type: 'text',   required: true },
      { name: 'description',    label: 'Description',      type: 'textarea' },
      { name: 'sortOrder',      label: 'Sort Order',       type: 'number' },
      { name: 'active',         label: 'Active',           type: 'checkbox', default: true },
      { name: 'status',         label: 'Status',           type: 'text',     default: 'Active' },
      { name: 'localNote',      label: 'Local Note',       type: 'text' }
    ]),
    payloadFromForm: (form, mode) => {
      const out = { ...form };
      if (mode === 'edit') delete out.subModuleId;
      if (out.sortOrder === '' || out.sortOrder == null) delete out.sortOrder;
      if (out.parentModuleId !== '' && out.parentModuleId != null) out.parentModuleId = parseInt(out.parentModuleId, 10);
      return out;
    },
    fromRow: (r) => ({
      subModuleId:    r.SubModuleId,
      parentModuleId: r.ParentModuleId,
      subModuleName:  r.SubModuleName || '',
      description:    r.Description || '',
      sortOrder:      r.SortOrder ?? '',
      active:         r.Active === true || r.Active === 1,
      status:         r.Status || 'Active',
      localNote:      r.LocalNote || ''
    })
  },
  screens: {
    label:    'Screens',
    icon:     Monitor,
    endpoint: '/uams/screens',
    idField:  'SeqId',
    nameField:'ScreenTitle',
    columns: [
      { field: 'SeqId',          header: 'Seq',         visible: true, frozen: true },
      { field: 'ScreenId',       header: 'ScreenId',    visible: true },
      { field: 'MenuTitle',      header: 'Menu Title',  visible: true },
      { field: 'ScreenTitle',    header: 'Screen Title',visible: true },
      { field: 'ScreenMenuName', header: 'Menu Name',   visible: false },
      { field: 'ScreenType',     header: 'Type',        visible: true },
      { field: 'ModuleId',       header: 'Module',      visible: true },
      { field: 'SubModuleId',    header: 'SubModule',   visible: true },
      { field: 'Status',         header: 'Status',      visible: true },
      { field: 'ModifiedAt',     header: 'Modified',    visible: false },
    ],
    formFields: (refs) => ([
      { name: 'seqId',          label: 'Seq Id (leave blank for auto)', type: 'number', editableOnAdd: true, editableOnEdit: false },
      { name: 'screenId',       label: 'Screen ID',      type: 'text' },
      { name: 'menuTitle',      label: 'Menu Title',     type: 'text', required: true },
      { name: 'screenTitle',    label: 'Screen Title',   type: 'text' },
      { name: 'screenMenuName', label: 'Screen Menu Name', type: 'text' },
      { name: 'screenType',     label: 'Screen Type',    type: 'text', default: 'M' },
      { name: 'moduleId',       label: 'Module',         type: 'select', required: true, optionsSource: 'modules' },
      { name: 'subModuleId',    label: 'SubModule',      type: 'select', optionsSource: 'submodules' },
      { name: 'status',         label: 'Status',         type: 'text', default: 'Active' },
      { name: 'localNote',      label: 'Local Note',     type: 'text' }
    ]),
    payloadFromForm: (form, mode) => {
      const out = { ...form };
      if (mode === 'edit') delete out.seqId;
      ['seqId'].forEach(k => { if (out[k] === '' || out[k] == null) delete out[k]; });
      if (out.moduleId !== '' && out.moduleId != null) out.moduleId = parseInt(out.moduleId, 10);
      if (out.subModuleId === '') out.subModuleId = null;
      return out;
    },
    fromRow: (r) => ({
      seqId:          r.SeqId,
      screenId:       r.ScreenId || '',
      menuTitle:      r.MenuTitle || '',
      screenTitle:    r.ScreenTitle || '',
      screenMenuName: r.ScreenMenuName || '',
      screenType:     r.ScreenType || 'M',
      moduleId:       r.ModuleId,
      subModuleId:    r.SubModuleId || '',
      status:         r.Status || 'Active',
      localNote:      r.LocalNote || ''
    })
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Manager
// ─────────────────────────────────────────────────────────────────────────────
export default function UAMSMasterManager() {
  const [tab, setTab] = useState('modules');
  const [refData, setRefData] = useState({ modules: [], submodules: [] });

  // Industry filter — '*' = all industries (no filter)
  const [industryCode, setIndustryCode] = useState(() => localStorage.getItem('uams_industry_filter') || '*');
  const [industries, setIndustries]     = useState([]);
  const [syncing, setSyncing]           = useState(false);
  const [lastSync, setLastSync]         = useState(null);

  useEffect(() => {
    apiFetch(`${API}/uams/industries?activeOnly=true`)
      .then(r => r.json())
      .then(d => setIndustries(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    try { localStorage.setItem('uams_industry_filter', industryCode); } catch (e) { /* noop */ }
  }, [industryCode]);

  // Reference dropdowns always use SHARED rows — SHARED is the master catalog
  // and the PK source of truth. Industry-specific rows are customisation copies;
  // mixing them with SHARED in a select causes duplicate options.
  const refreshRefs = useCallback(async () => {
    try {
      const [m, s] = await Promise.all([
        apiFetch(`${API}/uams/modules`).then(r => r.json()),
        apiFetch(`${API}/uams/submodules`).then(r => r.json())
      ]);
      setRefData({
        modules:    Array.isArray(m) ? m : [],
        submodules: Array.isArray(s) ? s : []
      });
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { refreshRefs(); }, [refreshRefs]);

  const onSyncFromTemplate = async () => {
    const templateLabel = industryCode === '*' ? 'RC26ERP_dev (default template)' : `${industryCode}'s configured template DB`;
    if (!window.confirm(
      `Sync UAMS catalog from ${templateLabel}?\n\n` +
      `Existing-wins by default: rows already in RC26masterDB will NOT be modified.\n` +
      `Only missing rows will be inserted${industryCode !== '*' ? `, and the ${industryCode} industry junction will be auto-populated.` : '.'}\n\n` +
      `You will be asked separately whether to overwrite existing data after the dry pass.`
    )) return;
    setSyncing(true);
    try {
      const body = industryCode && industryCode !== '*' ? { industryCode } : {};
      const res = await apiFetch(`${API}/uams/sync-from-template`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const d = await res.json();
      if (!res.ok || d.error) {
        erpToast.error(`Sync failed: ${d.error || res.status}${d.templateDB ? ` (template=${d.templateDB})` : ''}`);
        setSyncing(false);
        return;
      }
      setLastSync(d);
      const m = d.modules, s = d.subModules, sc = d.screens;
      const totalInserted = m.inserted + s.inserted + sc.inserted;
      const totalExisting = m.existing + s.existing + sc.existing;
      const junctions = d.industryMappings?.inserted || 0;

      let msg = `Sync complete from ${d.templateDB}:\n\u00b7 ${totalInserted} new rows inserted\n\u00b7 ${totalExisting} existing rows skipped (existing-wins)`;
      if (junctions > 0) msg += `\n\u00b7 ${junctions} industry junction rows added for ${d.industryCode}`;
      if (d.warnings?.length) msg += `\n\nWarnings: ${d.warnings.join(' \u00b7 ')}`;

      // If anything was found "existing", ask whether to force overwrite them.
      if (totalExisting > 0) {
        const confirmOverwrite = window.confirm(
          `${msg}\n\n` +
          `\u26a0\ufe0f  ${totalExisting} row(s) already exist in RC26masterDB.\n` +
          `Do you want to OVERWRITE them with the template DB values?\n\n` +
          `\u00b7 OK = Force-update existing rows (master-wins from template)\n` +
          `\u00b7 Cancel = Keep current RC26masterDB rows untouched (recommended)`
        );
        if (confirmOverwrite) {
          // Re-call with forceUpdate=true
          const res2 = await apiFetch(`${API}/uams/sync-from-template`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...body, forceUpdate: true })
          });
          const d2 = await res2.json();
          if (!res2.ok || d2.error) {
            erpToast.error(`Force-update failed: ${d2.error || res2.status}`);
          } else {
            setLastSync(d2);
            const m2 = d2.modules, s2 = d2.subModules, sc2 = d2.screens;
            erpToast.success(
              `Force-update OK: ${m2.updated}M / ${s2.updated}S / ${sc2.updated}Sc rows overwritten`
            );
          }
        } else {
          erpToast.info(
            totalInserted > 0
              ? `Inserted ${totalInserted} new rows. Existing rows preserved.`
              : `No changes \u2014 ${totalExisting} rows already present (existing-wins).`
          );
        }
      } else {
        erpToast.success(
          totalInserted > 0
            ? `Synced from ${d.templateDB}: ${totalInserted} new rows inserted` + (junctions > 0 ? ` \u00b7 ${junctions} junctions` : '')
            : `Already in sync with ${d.templateDB} \u2014 nothing to do.`
        );
      }
      refreshRefs();
    } catch (e) { erpToast.error(`Sync failed: ${e.message}`); }
    setSyncing(false);
  };

  const entity = ENTITIES[tab];
  const Icon = entity.icon;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Industry selector + Sync from Template action bar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
        padding: '10px 14px', borderRadius: 8,
        background: 'var(--surface-3, #f3f3f3)',
        border: '1px solid var(--border, #e2e8f0)'
      }} data-testid="uams-master-toolbar">
        <Building2 size={14} style={{ color: 'var(--brand-blue, #0078D4)' }} />
        <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Industry
        </label>
        <select
          value={industryCode}
          onChange={(e) => setIndustryCode(e.target.value)}
          data-testid="uams-master-industry"
          className="form-input"
          style={{ minWidth: 220, fontSize: 12, padding: '6px 10px', borderRadius: 6 }}
        >
          <option value="*">All industries (no filter)</option>
          {industries.map(i => (
            <option key={i.IndustryCode} value={i.IndustryCode}>
              {i.IndustryName || i.IndustryCode}
              {i.TemplateDB ? ` \u2014 ${i.TemplateDB}` : ''}
            </option>
          ))}
        </select>
        <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>
          {industryCode === '*'
            ? 'Showing all entities'
            : `Showing entities mapped to ${industryCode}`}
        </span>

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
          {industryCode !== '*' && (
            <button
              onClick={onSyncFromTemplate}
              disabled={syncing}
              className="erp-btn erp-btn-primary"
              data-testid="uams-sync-from-template-btn"
              title={`Import modules/submodules/screens from the template DB configured for ${industryCode} and map them to this industry`}
              style={{ fontSize: 11, gap: 5, display: 'inline-flex', alignItems: 'center' }}
            >
              <CloudDownload size={12} />
              {syncing ? 'Syncing…' : `Import from Template DB`}
            </button>
          )}
          {lastSync && (
            <span style={{ fontSize: 10, color: '#059669' }}>
              Last sync: {lastSync.templateDB} · {(lastSync.modules?.inserted || 0) + (lastSync.subModules?.inserted || 0) + (lastSync.screens?.inserted || 0)} rows added
            </span>
          )}
        </div>
      </div>

      <div style={{
        display: 'flex', alignItems: 'flex-start', gap: 10,
        padding: '10px 14px', borderRadius: 6,
        background: 'var(--erp-grid-row-hover, #eff6ff)',
        border: '1px solid var(--erp-border, #bfdbfe)',
        fontSize: 11, color: 'var(--brand-blue, #1e40af)'
      }}>
        <Database size={14} style={{ marginTop: 2, flexShrink: 0 }} />
        <div>
          <strong>UAMS Master — Local CRUD mode</strong> · Modules / SubModules / Screens are maintained <strong>directly in RC26MASTERDB</strong>.
          <br />
          <span style={{ color: '#64748b' }}>
            <strong>New industry has no modules?</strong> — Select the industry above, then click <em>Import from Template DB</em> to pull all modules/screens from its configured template DB and map them to that industry.
            &nbsp;·&nbsp;
            <strong>Add new module manually?</strong> — Switch to the <em>Modules</em> tab and click <em>+ Add New</em>.
            After adding, go to <em>Module Privileges</em> to set per-tier visibility.
          </span>
        </div>
      </div>

      {/* Sub-tabs */}
      <div style={{ display: 'flex', gap: 6, borderBottom: '1px solid #e2e8f0', paddingBottom: 8 }}>
        {Object.entries(ENTITIES).map(([key, e]) => {
          const I = e.icon;
          const active = tab === key;
          return (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={`uams-master-tab-${key}`}
              className="erp-btn"
              style={{
                background: active ? '#3b82f6' : 'transparent',
                color:      active ? '#f8fafc' : '#1e293b',
                borderColor:active ? '#3b82f6' : '#cbd5e1',
                fontWeight: active ? 700 : 600,
                gap: 6
              }}
            >
              <I size={12} /> {e.label}
            </button>
          );
        })}
      </div>

      <EntityGrid
        key={`${tab}-${industryCode}`}
        entity={entity}
        refData={refData}
        industryCode={industryCode}
        onAnyChange={refreshRefs}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// EntityGrid — wraps ERPAdvancedGrid with CRUD context menu + edit/add modal
// ─────────────────────────────────────────────────────────────────────────────
function EntityGrid({ entity, refData, industryCode, onAnyChange }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [page, setPage]         = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir]     = useState('asc');
  const [filters, setFilters]     = useState({});

  const [editing, setEditing] = useState(null); // { mode: 'add' | 'edit' | 'view' | 'duplicate', row }

  const load = useCallback(() => {
    setLoading(true);
    const qs = industryCode && industryCode !== '*' ? `?industryCode=${encodeURIComponent(industryCode)}` : '';
    apiFetch(`${API}${entity.endpoint}${qs}`)
      .then(r => r.json())
      .then(d => {
        if (d?.error) setError(d.error);
        else { setRows(Array.isArray(d) ? d : []); setError(null); }
        setLoading(false);
      })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [entity.endpoint, industryCode]);

  useEffect(() => { load(); }, [load]);

  const filteredSorted = useMemo(() => {
    let r = rows;
    const af = Object.entries(filters).filter(([_, v]) => v != null && String(v).trim() !== '');
    if (af.length) {
      r = r.filter(row => af.every(([k, v]) => String(row[k] ?? '').toLowerCase().includes(String(v).toLowerCase())));
    }
    if (sortField) {
      r = [...r].sort((a, b) => {
        const av = a[sortField], bv = b[sortField];
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        if (typeof av === 'number' && typeof bv === 'number') return sortDir === 'asc' ? av - bv : bv - av;
        return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
      });
    }
    return r;
  }, [rows, filters, sortField, sortDir]);

  const total = filteredSorted.length;
  const paginated = useMemo(() => filteredSorted.slice((page - 1) * pageSize, page * pageSize), [filteredSorted, page, pageSize]);

  const onSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  // ── Add / Edit / Duplicate ──────────────────────────────────────────────
  const startAdd = useCallback(() => {
    setEditing({ mode: 'add', row: null });
  }, []);
  const startEdit = useCallback((row) => setEditing({ mode: 'edit', row }), []);
  const startView = useCallback((row) => setEditing({ mode: 'view', row }), []);
  const startDuplicate = useCallback((row) => setEditing({ mode: 'duplicate', row }), []);

  // ── Delete ───────────────────────────────────────────────────────────────
  const onDelete = useCallback(async (row) => {
    const id = row[entity.idField];
    if (!window.confirm(`Delete ${entity.label} "${row[entity.nameField] || id}"?\nThis cannot be undone.`)) return;
    try {
      let res = await apiFetch(`${API}${entity.endpoint}/${encodeURIComponent(id)}`, { method: 'DELETE' });
      let data = await res.json();
      if (res.status === 409 && data.error && /HAS_CHILDREN|HAS_SCREENS/.test(data.error)) {
        if (window.confirm(`${data.error}. ${data.hint || 'Delete children too?'}`)) {
          res = await apiFetch(`${API}${entity.endpoint}/${encodeURIComponent(id)}?cascade=true`, { method: 'DELETE' });
          data = await res.json();
        } else {
          return;
        }
      }
      if (!res.ok || data.error) {
        erpToast.error(`Delete failed: ${data.error || res.status}`);
      } else {
        erpToast.success(`${entity.label} deleted`);
        load();
        onAnyChange && onAnyChange();
      }
    } catch (e) { erpToast.error(`Delete failed: ${e.message}`); }
  }, [entity, load, onAnyChange]);

  // ── Context menu ────────────────────────────────────────────────────────
  const { onRowContextMenu, ContextMenuPortal } = useERPContextMenu({
    idField: entity.idField,
    copyKeys: [{ field: entity.idField, label: `Copy ${entity.idField}` }],
    onView:    startView,
    onEdit:    startEdit,
    onPreview: (r) => {
      const lines = Object.entries(r).map(([k, v]) => `${k}: ${v ?? ''}`).join('\n');
      const w = window.open('', '_blank');
      w.document.write(`<pre style="font-family:monospace;font-size:12px;padding:20px;white-space:pre-wrap">${lines.replace(/</g, '&lt;')}</pre>`);
    },
    onPrint:   () => window.print(),
    onExport:  (r) => {
      const blob = new Blob([JSON.stringify(r, null, 2)], { type: 'application/json' });
      const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `${r[entity.idField] || 'row'}.json`; a.click();
    },
    extraItems: [
      { label: 'Duplicate', onClick: startDuplicate, testId: 'ctx-duplicate' },
      { label: 'Delete',    onClick: onDelete,       testId: 'ctx-delete', danger: true }
    ]
  });

  return (
    <div data-testid={`uams-master-grid-${entity.label.toLowerCase()}`}>
      {error && (
        <div style={{ padding: 10, background: '#fef2f2', color: '#b91c1c', borderRadius: 6, fontSize: 11, marginBottom: 8 }}>
          {error} <button onClick={load} className="erp-btn" style={{ marginLeft: 8 }}><RefreshCw size={11} /> Retry</button>
        </div>
      )}
      <ERPAdvancedGrid
        title={`UAMS ${entity.label} (${total})`}
        columns={entity.columns}
        data={paginated}
        total={total}
        idField={entity.idField}
        nameField={entity.nameField}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        sortField={sortField}
        sortDir={sortDir}
        onSort={onSort}
        filters={filters}
        onFilterChange={(f) => { setFilters(f); setPage(1); }}
        loading={loading}
        showAddNew={true}
        onAddNew={startAdd}
        onEdit={(row, mode) => mode === 'view' ? startView(row) : startEdit(row)}
        onPreview={startView}
        testId={`uams-master-${entity.label.toLowerCase()}`}
      />

      <RowContextDelegate
        gridTestId={`uams-master-${entity.label.toLowerCase()}`}
        rows={paginated}
        idField={entity.idField}
        onRowContextMenu={onRowContextMenu}
      />
      {ContextMenuPortal}

      {editing && (
        <EntityFormModal
          entity={entity}
          mode={editing.mode}
          row={editing.row}
          refData={refData}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); onAnyChange && onAnyChange(); }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// EntityFormModal — generic add/edit/view/duplicate form
// ─────────────────────────────────────────────────────────────────────────────
function EntityFormModal({ entity, mode, row, refData, onClose, onSaved }) {
  const isView = mode === 'view';
  const isAdd  = mode === 'add' || mode === 'duplicate';
  const fields = useMemo(() => entity.formFields(refData), [entity, refData]);

  const initial = useMemo(() => {
    if (row) {
      const base = entity.fromRow(row);
      if (mode === 'duplicate') {
        // Reset ID-like fields for duplication
        if ('moduleId'    in base) base.moduleId = '';
        if ('subModuleId' in base) base.subModuleId = '';
        if ('seqId'       in base) base.seqId = '';
        // Append marker to name fields to highlight the duplicate
        if (base.moduleName)    base.moduleName += ' (copy)';
        if (base.subModuleName) base.subModuleName += ' (copy)';
        if (base.menuTitle)     base.menuTitle += ' (copy)';
      }
      return base;
    }
    // Fresh add — pick defaults
    const def = {};
    for (const f of fields) {
      if ('default' in f) def[f.name] = f.default;
      else if (f.type === 'checkbox') def[f.name] = false;
      else def[f.name] = '';
    }
    return def;
  }, [entity, row, mode, fields]);

  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setForm(initial); }, [initial]);

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const submit = async (e) => {
    if (e) e.preventDefault();
    if (isView) { onClose(); return; }
    setSaving(true);
    try {
      const payload = entity.payloadFromForm(form, isAdd ? 'add' : 'edit');
      let res;
      if (isAdd) {
        res = await apiFetch(`${API}${entity.endpoint}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      } else {
        const id = row[entity.idField];
        res = await apiFetch(`${API}${entity.endpoint}/${encodeURIComponent(id)}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }
      const data = await res.json();
      if (!res.ok || data.error) {
        erpToast.error(`${isAdd ? 'Create' : 'Update'} failed: ${data.error || res.status}`);
      } else {
        erpToast.success(`${entity.label} ${isAdd ? 'created' : 'updated'}`);
        onSaved();
      }
    } catch (e) { erpToast.error(`Save failed: ${e.message}`); }
    setSaving(false);
  };

  const getSelectOptions = (source) => {
    if (source === 'modules')    return refData.modules.map(m => ({ value: m.ModuleId, label: `${m.ModuleId} · ${m.ModuleName}` }));
    if (source === 'submodules') {
      // If parent module is selected, filter
      const parent = form.moduleId;
      const all = refData.submodules;
      const list = parent ? all.filter(s => String(s.ParentModuleId) === String(parent)) : all;
      return list.map(s => ({ value: s.SubModuleId, label: `${s.SubModuleId} · ${s.SubModuleName}` }));
    }
    return [];
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(15, 23, 42, 0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 200
      }}
      onClick={onClose}
      data-testid="uams-entity-modal"
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--bg-surface, #fff)',
          color: 'var(--text-primary, #0f172a)',
          borderRadius: 10,
          padding: 20,
          minWidth: 480,
          maxWidth: '90vw',
          maxHeight: '85vh',
          overflowY: 'auto',
          boxShadow: '0 30px 60px rgba(15,23,42,0.4)'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>
            {isAdd ? `Add ${entity.label}` : isView ? `View ${entity.label}` : `Edit ${entity.label}`}
          </h3>
          <button type="button" onClick={onClose} className="erp-btn" style={{ padding: '4px 10px' }} data-testid="uams-entity-cancel">✕</button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {fields.map(f => {
            const disabled = isView || (mode === 'edit' && f.editableOnEdit === false) || (isAdd && f.editableOnAdd === false);
            const value = form[f.name];

            if (f.type === 'select') {
              const opts = f.options
                ? f.options.map(o => ({ value: o, label: o }))
                : getSelectOptions(f.optionsSource);
              return (
                <Field key={f.name} label={f.label} required={f.required} span={f.type === 'textarea' ? 2 : 1}>
                  <select
                    className="form-input"
                    value={value ?? ''}
                    onChange={(e) => setField(f.name, e.target.value)}
                    disabled={disabled}
                    required={f.required}
                    data-testid={`uams-form-${f.name}`}
                  >
                    <option value="">— select —</option>
                    {opts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </Field>
              );
            }
            if (f.type === 'checkbox') {
              return (
                <Field key={f.name} label={f.label} span={1}>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 0' }}>
                    <input
                      type="checkbox"
                      checked={!!value}
                      onChange={(e) => setField(f.name, e.target.checked)}
                      disabled={disabled}
                      data-testid={`uams-form-${f.name}`}
                    />
                    <span style={{ fontSize: 12 }}>{f.label}</span>
                  </label>
                </Field>
              );
            }
            if (f.type === 'textarea') {
              return (
                <Field key={f.name} label={f.label} span={2}>
                  <textarea
                    className="form-input"
                    value={value ?? ''}
                    onChange={(e) => setField(f.name, e.target.value)}
                    disabled={disabled}
                    rows={3}
                    data-testid={`uams-form-${f.name}`}
                  />
                </Field>
              );
            }
            return (
              <Field key={f.name} label={f.label} required={f.required} span={1}>
                <input
                  className="form-input"
                  type={f.type === 'number' ? 'number' : 'text'}
                  value={value ?? ''}
                  onChange={(e) => setField(f.name, e.target.value)}
                  disabled={disabled}
                  required={f.required}
                  data-testid={`uams-form-${f.name}`}
                />
              </Field>
            );
          })}
        </div>

        {!isView && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
            <button type="button" onClick={onClose} className="erp-btn" disabled={saving} data-testid="uams-entity-cancel-bottom">Cancel</button>
            <button type="submit" className="erp-btn erp-btn-primary" disabled={saving} data-testid="uams-entity-save">
              {saving ? 'Saving…' : (isAdd ? <><Plus size={12} /> Create</> : 'Save')}
            </button>
          </div>
        )}
        {isView && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
            <button type="button" onClick={onClose} className="erp-btn erp-btn-primary" data-testid="uams-entity-close-view">Close</button>
          </div>
        )}
      </form>
    </div>
  );
}

function Field({ label, required, span = 1, children }) {
  return (
    <div style={{ gridColumn: `span ${span}` }}>
      <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
        {label} {required && <span style={{ color: '#ef4444' }}>*</span>}
      </label>
      {children}
    </div>
  );
}

// Re-implemented (instead of importing) so we don't need to refactor LicensorConsole.jsx
function RowContextDelegate({ gridTestId, rows, idField, onRowContextMenu }) {
  const ref = useRef({ rows, idField });
  ref.current = { rows, idField };
  useEffect(() => {
    const handler = (e) => {
      const tr = e.target.closest(`tr[data-testid^="${gridTestId}-row-"]`);
      if (!tr) return;
      const idAttr = tr.getAttribute('data-testid');
      const id = idAttr.replace(`${gridTestId}-row-`, '');
      const row = ref.current.rows.find(r => String(r[ref.current.idField]) === id);
      if (row) onRowContextMenu(e, row);
    };
    document.addEventListener('contextmenu', handler);
    return () => document.removeEventListener('contextmenu', handler);
  }, [gridTestId, onRowContextMenu]);
  return null;
}
